import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Phone, ArrowRight } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "@/context/AuthContext";

const Register = () => {
  const navigate = useNavigate();
  const { register, login } = useAuth();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });
  const [terms, setTerms] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!terms) {
      setError("Debes aceptar los términos y la política de privacidad.");
      return;
    }
    if (form.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    setSubmitting(true);
    try {
      const fullName = `${form.firstName} ${form.lastName}`.trim();
      await register({
        email: form.email,
        password: form.password,
        fullName,
        phone: form.phone || null,
      });
      // Registro y login son endpoints separados en el backend a propósito
      // (crear cuenta vs. autenticar) — acá se encadenan para que el
      // usuario no tenga que volver a escribir sus credenciales.
      const user = await login(form.email, form.password);
      navigate(user.role === "cliente" ? "/cliente" : "/", { replace: true });
    } catch (err) {
      if (err.response?.status === 409) {
        setError("Ese correo o teléfono ya está registrado.");
      } else if (err.response?.status === 422) {
        setError("Revisa los datos ingresados — algún campo no tiene un formato válido.");
      } else if (err.response?.status === 429) {
        setError("Demasiados intentos. Espera un minuto e intenta de nuevo.");
      } else {
        setError("No se pudo conectar con el servidor. Intenta de nuevo.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0E10] text-[#F5F5F5] grain flex items-center justify-center px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(201,162,39,0.08),transparent_60%)] pointer-events-none" />
      <div className="relative w-full max-w-lg">
        <div className="flex justify-center mb-8">
          <Logo size="lg" />
        </div>
        <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-8" data-testid="register-card">
          <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display mb-2">Nueva cuenta</p>
          <h1 className="font-display font-bold text-2xl uppercase tracking-wide">Crea tu perfil ejecutivo</h1>
          <p className="text-sm text-[#A0A0A5] font-body mt-2">Comienza a reservar traslados premium en 30 segundos.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={User} label="Nombres" placeholder="Alejandra" testId="reg-firstname" required value={form.firstName} onChange={update("firstName")} />
              <Field icon={User} label="Apellidos" placeholder="Villarán" testId="reg-lastname" required value={form.lastName} onChange={update("lastName")} />
            </div>
            <Field icon={Mail} label="Correo corporativo" placeholder="tu@empresa.pe" testId="reg-email" type="email" required value={form.email} onChange={update("email")} />
            <Field icon={Phone} label="Teléfono" placeholder="+51 999 000 000" testId="reg-phone" value={form.phone} onChange={update("phone")} />
            <Field icon={Lock} type="password" label="Contraseña" placeholder="••••••••" testId="reg-password" required value={form.password} onChange={update("password")} />

            {error && (
              <p className="text-xs text-red-400 font-body" data-testid="register-error">
                {error}
              </p>
            )}

            <label className="flex items-start gap-2 text-xs text-[#A0A0A5] font-body">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5 accent-[#C9A227]"
                data-testid="reg-terms"
              />
              <span>
                Acepto los <a href="#" className="text-[#C9A227]">términos</a> y la{" "}
                <a href="#" className="text-[#C9A227]">política de privacidad</a> de LEP.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#C9A227] hover:bg-[#D4AF37] disabled:opacity-60 disabled:cursor-not-allowed text-[#0D0E10] font-display font-bold uppercase tracking-wider text-sm rounded-lg transition"
              data-testid="register-submit-btn"
            >
              {submitting ? "Creando cuenta…" : "Crear cuenta"}
              <ArrowRight size={14} />
            </button>
          </form>

          <div className="hr-gold my-7" />
          <p className="text-center text-xs text-[#A0A0A5] font-body">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-[#C9A227] uppercase tracking-wider font-display" data-testid="register-to-login">
              Iniciar sesión
            </Link>
          </p>
        </div>
        <p className="mt-6 text-center text-[10px] text-[#A0A0A5] uppercase tracking-brand font-display">
          <Link to="/" className="hover:text-[#C9A227]">← Volver al inicio</Link>
        </p>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, testId, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display block mb-2">{label}</label>
    <div className="flex items-center gap-2 bg-[#0D0E10] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-3.5 py-3 transition-colors">
      <Icon size={14} className="text-[#C9A227]" />
      <input {...props} className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F5] font-body placeholder:text-[#5A5B60]" data-testid={testId} />
    </div>
  </div>
);

export default Register;
